// Main entry point - News App
import { currentCategory, setCurrentCategory, setExpandedStoryId, isSignedIn } from './state.js';
import { initTheme, applyTheme } from './theme.js';
import { checkAuthStatus, initGoogleSignIn, signOut, triggerGoogleSignIn, skipSignin, setAuthDependencies } from './auth.js';
import { loadCategories, closeCategoryPicker } from './categories.js';
import { loadStories, toggleStory, renderStories } from './stories.js';
import { handleFeedback, dismissFeedbackNudge } from './feedback.js';
import { openBlockModal, closeBlockModal, confirmBlock, setDependencies as setBlockingDeps, showBlockModalForStory } from './blocking.js';
import { openSettings, closeSettings, unblockItem } from './settings.js';
import { toggleHistoryExpand, clearReadingHistory, removeFromHistory } from './history.js';
import { toggleEmailDigest, setEmailFrequency, setEmailSchedule, updateEmailCategories, sendTestEmail } from './email.js';
import { changeLayout, toggleLayout, updateLayoutToggle, changeFontSize, applyFontSize, updateFontSizeSelector } from './layout.js';
import { checkFirstVisit, handleWelcomeSignIn, handleWelcomeSkip } from './welcome.js';
import { shareStory } from './share.js';
import { loadBookmarks, toggleBookmark } from './bookmarks.js';
import { getStreakData, renderStreakBadge } from './streaks.js';
import { initPushNotifications, subscribeToPush } from './pushNotifications.js';

// New features
import { initKeyboardNavigation, resetKeyboardSelection } from './keyboard.js';
import { showNewsDietDashboard, loadNewsDiet } from './newsDiet.js';

// Wire up module dependencies
setBlockingDeps(loadStories, renderStories);
setAuthDependencies(loadStories, showBlockModalForStory);

// Handle shared story links
function handleSharedLink() {
  const params = new URLSearchParams(window.location.search);
  const sharedStoryId = params.get('story');
  const sharedCategory = params.get('category');
  
  if (sharedStoryId && sharedCategory) {
    setCurrentCategory(sharedCategory);
    setExpandedStoryId(sharedStoryId);
    window.history.replaceState({}, '', window.location.pathname);
    return true;
  }
  return false;
}

// Load saved feature interests
function loadSavedInterests() {
  const saved = localStorage.getItem('featureInterests');
  if (saved) {
    try {
      const interests = JSON.parse(saved);
      document.querySelectorAll('.feature-checkbox input').forEach(cb => {
        if (interests.includes(cb.value)) cb.checked = true;
      });
    } catch (e) {}
  }
}

// Setup event listeners
function setupEventListeners() {
  // Settings
  document.getElementById('settings-btn')?.addEventListener('click', openSettings);
  document.getElementById('settings-close')?.addEventListener('click', closeSettings);

  // Theme toggles
  document.querySelectorAll('.theme-option').forEach(btn => {
    btn.addEventListener('click', () => applyTheme(btn.dataset.theme));
  });

  // Tagline opens settings
  document.getElementById('tagline')?.addEventListener('click', openSettings);

  // Category picker
  document.getElementById('category-picker-close')?.addEventListener('click', closeCategoryPicker);

  // Layout options
  document.querySelectorAll('.layout-option').forEach(option => {
    option.addEventListener('click', () => {
      option.querySelector('input').checked = true;
      changeLayout(option.dataset.layout);
    });
  });

  // Layout toggle button
  document.getElementById('layout-toggle')?.addEventListener('click', toggleLayout);

  // Font size buttons
  document.querySelectorAll('.font-size-btn').forEach(btn => {
    btn.addEventListener('click', () => changeFontSize(btn.dataset.size));
  });

  // Sign-in prompt
  document.getElementById('signin-prompt-skip')?.addEventListener('click', skipSignin);
  document.getElementById('signin-prompt-google')?.addEventListener('click', triggerGoogleSignIn);
  document.getElementById('google-signin-btn')?.addEventListener('click', triggerGoogleSignIn);

  // Welcome modal
  document.getElementById('welcome-google-signin')?.addEventListener('click', handleWelcomeSignIn);
  document.getElementById('welcome-skip')?.addEventListener('click', handleWelcomeSkip);

  // Block modal
  document.getElementById('block-cancel')?.addEventListener('click', closeBlockModal);
  document.getElementById('block-confirm')?.addEventListener('click', confirmBlock);

  // Email digest controls
  document.getElementById('email-digest-toggle')?.addEventListener('change', (e) => toggleEmailDigest(e.target.checked));
  document.querySelectorAll('.freq-btn').forEach(btn => {
    btn.addEventListener('click', () => setEmailFrequency(btn.dataset.freq));
  });
  document.querySelectorAll('.sched-btn').forEach(btn => {
    btn.addEventListener('click', () => setEmailSchedule(btn.dataset.sched));
  });
  document.querySelectorAll('#email-categories input').forEach(cb => {
    cb.addEventListener('change', updateEmailCategories);
  });
  document.querySelector('.test-email-btn')?.addEventListener('click', sendTestEmail);

  // History
  document.getElementById('clear-history-btn')?.addEventListener('click', clearReadingHistory);

  // User profile
  document.getElementById('user-profile')?.addEventListener('click', openSettings);
  document.getElementById('sign-out-btn')?.addEventListener('click', signOut);
  
  // Push notification toggle
  document.getElementById('push-notifications-btn')?.addEventListener('click', subscribeToPush);
  
  // News diet dashboard button
  document.getElementById('news-diet-btn')?.addEventListener('click', showNewsDietDashboard);
}

// Expose functions to window for onclick handlers in HTML
window.appToggleStory = toggleStory;
window.appHandleFeedback = handleFeedback;
window.appOpenBlockModal = openBlockModal;
window.appShareStory = shareStory;
window.appUnblockItem = unblockItem;
window.appRemoveFromHistory = removeFromHistory;
window.appToggleHistoryExpand = toggleHistoryExpand;
window.appToggleBookmark = toggleBookmark;
window.dismissFeedbackNudge = dismissFeedbackNudge;
window.appShowNewsDiet = showNewsDietDashboard;

// Initialize app
async function init() {
  initTheme();
  handleSharedLink();
  applyFontSize();
  updateLayoutToggle();
  updateFontSizeSelector();
  loadSavedInterests();
  
  // Init new features
  initKeyboardNavigation();
  
  // Init push notifications (non-blocking)
  initPushNotifications();
  
  await checkAuthStatus();
  await initGoogleSignIn();
  await loadCategories();
  await Promise.all([
    loadBookmarks(),
    loadNewsDiet()
  ]);
  await loadStories(currentCategory);
  
  // Reset keyboard selection when stories load
  resetKeyboardSelection();
  
  setupEventListeners();
  checkFirstVisit();
  
  // Load and display streak if signed in
  if (isSignedIn) {
    const streakData = await getStreakData();
    renderStreakBadge(document.getElementById('streak-container'), streakData);
  }
}

// Start the app
init();
